// New homepage uses Sukhumvit Set (loaded via @font-face on the /new page only).
// Kanit / system fonts remain as graceful fallbacks while the webfont loads.
const systemFontStack =
  '"Sukhumvit Set", Kanit, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'

export const T = {
  font: {
    heading: systemFontStack,
    body: systemFontStack,
  },
  color: {
    brand: '#009EFF',
    brandDeep: '#0066CC',
    brandDark: '#004C99',
    accent: '#FF7A1A',
    ink: '#1A1A2E',
    inkSoft: '#333333',
    muted: '#666666',
    faint: '#999999',
    surface: '#FFFFFF',
    canvas: '#F5F9FF',
    canvasAlt: '#EBF3FF',
    line: '#E0E8F0',
    onBrand: '#FFFFFF',
    success: '#16A34A',
  },
  gradient: {
    brand: 'linear-gradient(135deg, #0066CC 0%, #009EFF 55%, #33C9FF 100%)',
    brandSoft: 'linear-gradient(180deg, #F0F7FF 0%, #FFFFFF 100%)',
    hero: 'linear-gradient(180deg, #E8F4FD 0%, #FFFFFF 50%)',
    carrierBar: 'linear-gradient(90deg, #0066CC 0%, #009EFF 50%, #33C9FF 100%)',
    blue: 'linear-gradient(135deg, #0066CC 0%, #009EFF 100%)',
  },
  radius: { sm: 8, md: 12, lg: 16, xl: 24, pill: 999 },
  // Frosted-glass panel treatment for the check-price card + its active tab
  // (matches the design mockup: the world-map/globe hero art shows faintly
  // through the card). `panel` is `color.surface` (#FFFFFF) with alpha, not
  // a new hue — kept alpha high enough that dark ink text (color.ink) stays
  // comfortably above AA contrast even over the busiest part of the banner.
  glass: {
    panel: 'rgba(255, 255, 255, 0.82)',
    blur: 'blur(12px)',
  },
  shadow: {
    sm: '0 2px 8px rgba(0,0,0,0.06)',
    md: '0 4px 16px rgba(0,0,0,0.08)',
    lg: '0 8px 32px rgba(0,0,0,0.12)',
    card: '0 4px 20px rgba(0,102,204,0.08)',
  },
  ease: {
    out: 'cubic-bezier(0.22, 1, 0.36, 1)',
    inOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  container: 1200,
  // Conic-gradient color stops for the shared "specular ring" CTA treatment
  // (see `specularRing` below), tinted per button family.
  ring: {
    // Bright blue/cyan ring for solid blue-filled pills (check-price submit,
    // track button, header "ลูกค้าองค์กร").
    blue: '#009EFF26, #33C9FF, #B3ECFF, #33C9FF, #009EFF26',
    // Softer blue ring for the WHITE pill on the blue band
    // (ShippopBusinessNew) — reads on white without harshness.
    blueSoft:
      'rgba(0,102,204,0.06), rgba(0,158,255,0.4), rgba(179,236,255,0.85), rgba(0,158,255,0.4), rgba(0,102,204,0.06)',
  },
}

// Shared "specular ring" mixin — the one unifying CTA treatment across /new:
// a continuously-rotating conic-gradient outline (masked down to a thin
// border via WebkitMask/mask exclude), generalized from the check-price
// submit button's original rotating ring. `gradient` is a conic-gradient
// color-stop list (tinted per button, see `T.ring` above); `padding`
// controls ring thickness. Always-on (not hover-gated) — driven by the
// shared `--promo-angle` custom property + `promo-spin` keyframes
// registered globally in pages/new/index.js's headEl <style> (JSS can't
// emit @property). Where @property isn't supported, `var(--promo-angle,
// 0deg)` falls back to a fixed angle, so the ring degrades to a static
// outline instead of animating or breaking.
//
// Spread the returned object under a `'&::before'` key, and spread
// `specularHost` onto the button's own class so it has the positioning the
// ring pseudo-element needs.
export const specularRing = (gradient, padding = '1px') => ({
  content: '""',
  position: 'absolute',
  inset: 0,
  borderRadius: 'inherit',
  zIndex: -1,
  padding,
  pointerEvents: 'none',
  background: `conic-gradient(from var(--promo-angle, 0deg), ${gradient})`,
  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  WebkitMaskComposite: 'xor',
  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  maskComposite: 'exclude',
  animation: 'promo-spin 4s linear infinite',
})

// Base positioning any button needs to host `specularRing` via ::before,
// plus the shared hover-lift so every unified CTA feels like one family.
// (No reduced-motion override here for the hover-lift itself: it's a small,
// user-triggered 2px transform, not an auto-playing animation — the thing
// prefers-reduced-motion is about is the continuous ring spin and the
// ambient hero motion, which each get their own explicit opt-out.)
export const specularHost = {
  position: 'relative',
  isolation: 'isolate',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
}

// Shared reduced-motion override for the ring spin — spread this under a
// `'@media (prefers-reduced-motion: reduce)'` key (merge it with any other
// reduced-motion rules the component already has; JSS/plain-object spread
// does not deep-merge duplicate keys).
export const specularRingReduced = {
  '&::before': { animation: 'none' },
}

export default T
